import React, { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useEditPhoto } from "../../Hooks/useEditPhoto";
import { request, Request_Post_Axios } from "../../../API/index";

const EditPhotoPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const mainFileRef = useRef(null);
  const galleryFileRef = useRef(null);

  const {
    formData,
    mainImgPreview,
    newGalleryFiles,
    handleInputChange,
    changeMainImg,
    removeMainImg,
    addGalleryFiles,
    removeNewFile,
    removeExistingImg,
  } = useEditPhoto(state?.post);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();

    formDataToSend.append("title", formData.title);
    formDataToSend.append("date", formData.date);
    formDataToSend.append("desc", formData.desc);
    formDataToSend.append("imgUrl", formData.imgUrl); // 기존 대표사진 파일명

    formDataToSend.append("existingGallery", JSON.stringify(formData.gallery));

    if (mainFileRef.current && mainFileRef.current.files.length > 0) {
      formDataToSend.append("newMain", mainFileRef.current.files[0]);
    }

    if (newGalleryFiles.length > 0) {
      newGalleryFiles.forEach((item) => {
        formDataToSend.append("newGallery", item.file);
      });
    }

    try {
      // 6. 서버로 전송
      const response = await request.post(
        `/Dashboard/updateImages/${state?.post?.id}`,
        formDataToSend,
      );

      if (response.status === 200) {
        alert(response.data.message + "되었습니다.");
        navigate(-1);
      } else {
        alert(`저장 실패: ${response.data.message}`);
      }
    } catch (error) {
      console.error("API 전송 에러:", error);
      alert("서버와 통신 중 에러가 발생했습니다.");
    }
  };
  return (
    <Container>
      <Header>
        <button onClick={() => navigate(-1)}>← 뒤로가기</button>
        <h1>게시판 수정</h1>
      </Header>

      <FormCard onSubmit={handleSubmit}>
        <InputGroup>
          <label>게시물 제목</label>
          <input
            name="title"
            value={formData.title}
            onChange={handleInputChange}
          />
        </InputGroup>

        <InputGroup>
          <label>상세 설명</label>
          <textarea
            name="desc"
            rows="4"
            value={formData.desc}
            onChange={handleInputChange}
          />
        </InputGroup>

        <PhotoSection>
          <label>대표 사진</label>
          <MainPhotoCard>
            {mainImgPreview ? (
              <>
                <img src={mainImgPreview} alt="Preview" />
                <PhotoActions>
                  <ActionButton
                    type="button"
                    onClick={() => mainFileRef.current.click()}
                  >
                    변경
                  </ActionButton>
                  <ActionButton
                    type="button"
                    className="delete"
                    onClick={removeMainImg}
                  >
                    삭제
                  </ActionButton>
                </PhotoActions>
              </>
            ) : (
              <EmptyPhoto onClick={() => mainFileRef.current.click()}>
                + 사진 등록
              </EmptyPhoto>
            )}
            <input
              type="file"
              ref={mainFileRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={(e) => {
                const selectedFile = e.target.files;
                if (selectedFile) {
                  changeMainImg(selectedFile[0]);
                }
              }}
            />
          </MainPhotoCard>
        </PhotoSection>

        <PhotoSection>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <label style={{ margin: 0 }}>
              갤러리 관리 ({formData.gallery.length + newGalleryFiles.length})
            </label>
            <AddBtn
              type="button"
              onClick={() => galleryFileRef.current.click()}
            >
              + 사진 추가
            </AddBtn>
            <input
              type="file"
              ref={galleryFileRef}
              style={{ display: "none" }}
              multiple
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  addGalleryFiles(Array.from(e.target.files));
                }
              }}
            />
          </div>

          <GalleryGrid>
            {/* 기존 이미지 */}
            {formData.gallery.map((img, idx) => (
              <GalleryCard key={`old-${idx}`}>
                <img
                  src={`${process.env.REACT_APP_BACK_API_URL}/company/${img}`}
                  alt="Existing"
                />
                <DeleteBadge onClick={() => removeExistingImg(img)}>
                  ×
                </DeleteBadge>
              </GalleryCard>
            ))}
            {/* 신규 이미지 */}
            {newGalleryFiles.map((item) => (
              <GalleryCard key={item.id} isNew>
                <img src={item.preview} alt="New" />
                <DeleteBadge onClick={() => removeNewFile(item.id)}>
                  ×
                </DeleteBadge>
                <NewLabel>NEW</NewLabel>
              </GalleryCard>
            ))}
          </GalleryGrid>
        </PhotoSection>

        <ButtonGroup>
          <CancelBtn type="button" onClick={() => navigate(-1)}>
            취소
          </CancelBtn>
          <SaveBtn type="submit">모든 변경사항 저장</SaveBtn>
        </ButtonGroup>
      </FormCard>
    </Container>
  );
};

/* --- Styled Components는 이전과 동일 --- */
const Container = styled.div`
  padding: 3rem;
  max-width: 900px;
  margin: 0 auto;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  h1 {
    font-size: 1.6rem;
    font-weight: 700;
  }
  button {
    background: none;
    border: none;
    cursor: pointer;
    color: #64748b;
  }
`;
const FormCard = styled.form`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
`;
const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #334155;
  }
  input,
  textarea {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
  }
`;
const PhotoSection = styled.div`
  margin-bottom: 2.5rem;
`;
const MainPhotoCard = styled.div`
  width: 300px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const PhotoActions = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.6);
  padding: 5px;
  text-align: center;
`;
const ActionButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 0.8rem;
  cursor: pointer;
  &.delete {
    color: #ff4d4f;
    margin-left: 10px;
  }
`;
const EmptyPhoto = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: pointer;
`;
const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 15px;
  border: 1px solid #e2e8f0;
`;
const GalleryCard = styled.div`
  position: relative;
  height: 120px;
  border-radius: 10px;
  overflow: hidden;
  border: ${(props) =>
    props.isNew ? "2px solid #2563eb" : "1px solid #cbd5e1"};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const NewLabel = styled.span`
  position: absolute;
  bottom: 5px;
  left: 5px;
  background: #2563eb;
  color: white;
  font-size: 10px;
  padding: 2px 5px;
  border-radius: 4px;
  font-weight: 800;
`;
const DeleteBadge = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  background: #ef4444;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
`;
const AddBtn = styled.button`
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
`;
const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;
const SaveBtn = styled.button`
  flex: 2;
  padding: 1rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
`;
const CancelBtn = styled.button`
  flex: 1;
  padding: 1rem;
  background: #f1f5f9;
  border-radius: 12px;
  border: none;
  cursor: pointer;
`;

export default EditPhotoPage;
